import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { HOLIDAY_DAY_NOTE_LEGEND, holidayDayNote } from '@/features/institute/holidays/utils';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type { Holiday } from '@/types/holiday';
import type {
  SectionTimetables,
  TimetablePublication,
  TimetableVersion,
} from '@/types/timetableAdmin';
import { instituteTimetableApi } from '../api/timetable.api';
import { describeTimetableRange } from '../timetableRange';

/** What "Start with" is called when nothing is copied. */
const EMPTY = '';

export interface TimetableDatesModalProps {
  /** The section, its session and its other timetables, as the server listed them. */
  sectionTimetables: SectionTimetables;
  /** The timetable whose dates are being changed. Omitted: a new timetable is started. */
  timetable?: TimetableVersion;
  /**
   * The Academic Calendar's holidays, marked on both date pickers so the office
   * can see which days the school is closed while choosing the dates. Null
   * while they load, or if they failed to — the pickers then mark nothing.
   */
  holidays: Holiday[] | null;
  /** Where a new timetable's start date opens — usually the week on screen. */
  defaultFrom?: string;
  onClose: () => void;
  /** The timetable as saved, so the page can move to it. */
  onSaved: (timetable: TimetableVersion) => void;
}

/**
 * Starting a timetable for a section, or moving one's dates.
 *
 * One form for both, because both are the same two dates under the same
 * rules: inside the session, the end not before the start, and clear of the
 * section's other timetables. The first two are checked here for fast feedback
 * and bound the pickers; the overlap and any teacher clash the new dates would
 * cause are the server's to decide, and its refusal is shown as it arrives.
 *
 * A new timetable can start as a copy of another of the section's timetables,
 * because a revised timetable is usually the last one with a few cells moved.
 */
export function TimetableDatesModal({
  sectionTimetables,
  timetable,
  holidays,
  defaultFrom,
  onClose,
  onSaved,
}: TimetableDatesModalProps) {
  const { section, academicYear, timetables } = sectionTimetables;
  const isEdit = Boolean(timetable);

  const [effectiveFrom, setEffectiveFrom] = useState(
    timetable?.effectiveFrom ?? clampToYear(defaultFrom ?? academicYear.startDate),
  );
  const [effectiveTo, setEffectiveTo] = useState(timetable?.effectiveTo ?? '');
  const [copyFromUuid, setCopyFromUuid] = useState(EMPTY);
  /** Which of the two create buttons is saving, so only that one spins. */
  const [savingAs, setSavingAs] = useState<TimetablePublication | null>(null);
  const isSaving = savingAs !== null;

  function clampToYear(day: string) {
    if (day < academicYear.startDate) return academicYear.startDate;
    if (day > academicYear.endDate) return academicYear.endDate;
    return day;
  }

  const holidayName = holidayDayNote(holidays);

  const fromError = effectiveFrom ? undefined : 'Choose the first day it applies';
  const toError =
    effectiveTo && effectiveFrom && effectiveTo < effectiveFrom
      ? 'The end date cannot be before the start date'
      : undefined;

  const copyOptions = [
    { value: EMPTY, label: 'An empty timetable' },
    ...timetables.map((option) => ({
      value: option.uuid,
      label: `A copy of ${describeTimetableRange(option)} (${option.lessons} ${option.lessons === 1 ? 'lesson' : 'lessons'})`,
    })),
  ];

  /**
   * Saves the dates. A new timetable is created published unless the draft
   * button was pressed — Enter, like the main button, publishes, as it always has.
   */
  async function save(publication: TimetablePublication) {
    if (isSaving || fromError || toError) return;

    const dates = { effectiveFrom, effectiveTo: effectiveTo || undefined };

    setSavingAs(publication);
    try {
      const saved = timetable
        ? await instituteTimetableApi.updateTimetableDates(timetable.uuid, dates)
        : await instituteTimetableApi.createTimetable(section.uuid, {
            ...dates,
            copyFromUuid: copyFromUuid || undefined,
            publication,
          });

      toast.success(
        isEdit
          ? 'Timetable dates updated'
          : saved.publication === 'DRAFT'
            ? 'Timetable saved as a draft'
            : 'Timetable created',
        saved.publication === 'DRAFT' && !isEdit
          ? `${section.displayName}: ${describeTimetableRange(saved)}. Only the office can see it — publish it when it is ready.`
          : `${section.displayName}: ${describeTimetableRange(saved)}.`,
      );
      onSaved(saved);
      onClose();
    } catch (caught) {
      toast.error(
        isEdit ? 'Could not change those dates' : 'Could not create the timetable',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setSavingAs(null);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void save('PUBLISHED');
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Change timetable dates' : `New timetable for ${section.displayName}`}
      description={`It applies only between these dates, inside ${academicYear.name}. A section follows one timetable on any day, so the dates cannot overlap its others.`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          {/* Moving dates keeps whatever state the timetable is in. */}
          {!isEdit && (
            <Button
              variant="secondary"
              size="md"
              onClick={() => void save('DRAFT')}
              isLoading={savingAs === 'DRAFT'}
              disabled={Boolean(fromError || toError) || savingAs === 'PUBLISHED'}
            >
              Save as draft
            </Button>
          )}
          <Button
            variant="primary"
            size="md"
            type="submit"
            form="timetable-dates-form"
            isLoading={savingAs === 'PUBLISHED'}
            disabled={Boolean(fromError || toError) || savingAs === 'DRAFT'}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
          >
            {isEdit ? 'Save dates' : 'Create timetable'}
          </Button>
        </>
      }
    >
      <form
        id="timetable-dates-form"
        onSubmit={handleSubmit}
        noValidate
        className="flex flex-col gap-4 py-1"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DatePicker
            label="Applies from"
            required
            value={effectiveFrom}
            min={academicYear.startDate}
            max={academicYear.endDate}
            error={fromError}
            dayNote={holidayName}
            dayNoteLegend={HOLIDAY_DAY_NOTE_LEGEND}
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
          <DatePicker
            label="Applies to (optional)"
            value={effectiveTo}
            min={effectiveFrom || academicYear.startDate}
            max={academicYear.endDate}
            isClearable
            error={toError}
            dayNote={holidayName}
            dayNoteLegend={HOLIDAY_DAY_NOTE_LEGEND}
            hint="Leave blank to run until the session ends."
            onChange={(event) => setEffectiveTo(event.target.value)}
          />
        </div>

        {!isEdit && timetables.length > 0 && (
          <Select
            label="Start with"
            options={copyOptions}
            value={copyFromUuid}
            onChange={(event) => setCopyFromUuid(event.target.value)}
            hint="A copy brings every lesson across, so only the changes need making."
          />
        )}
      </form>
    </Modal>
  );
}
